// ===== ROLE DEFINITIONS =====
// Mirrors backend: app/api/src/common/constants/roles.constant.ts
export enum UserRole {
  ADMIN = 'admin',
  DEVELOPER = 'developer',
  USER = 'user',
}

// ===== SYSTEM FUNCTIONS =====
// Mirrors backend SystemFunction enum
export enum SystemFunction {
  // Organization Management
  VIEW_ORGANIZATION = 'view-organization',
  EDIT_ORGANIZATION = 'edit-organization',
  DELETE_ORGANIZATION = 'delete-organization',
  MANAGE_BILLING = 'manage-billing',

  // Project Management
  VIEW_PROJECTS = 'view-projects',
  CREATE_PROJECTS = 'create-projects',
  EDIT_PROJECTS = 'edit-projects',
  DELETE_PROJECTS = 'delete-projects',

  // Integration Management
  VIEW_INTEGRATIONS = 'view-integrations',
  MANAGE_INTEGRATIONS = 'manage-integrations',

  // Knowledge Base Management
  VIEW_KNOWLEDGE_BASE = 'view-knowledge-base',
  MANAGE_KNOWLEDGE_BASE = 'manage-knowledge-base',

  // User Management
  VIEW_USERS = 'view-users',
  INVITE_USERS = 'invite-users',
  MANAGE_USERS = 'manage-users',

  // Analytics & Monitoring
  VIEW_ANALYTICS = 'view-analytics',

  // Agent Configuration
  VIEW_AGENT_CONFIG = 'view-agent-config',
  EDIT_AGENT_CONFIG = 'edit-agent-config',
}

// ===== PERMISSION LEVELS =====
// Mirrors backend Permission enum
export enum Permission {
  NO_ACCESS = 'N',
  READ = 'R',
  EDIT = 'E',
}

// ===== PERMISSION MATRIX =====
// This is the ONLY file you need to update when adding new roles
// Mirrors backend ROLE_PERMISSIONS matrix
export const ROLE_PERMISSIONS: Record<
  UserRole,
  Partial<Record<SystemFunction, Permission>>
> = {
  [UserRole.ADMIN]: {
    [SystemFunction.VIEW_ORGANIZATION]: Permission.EDIT,
    [SystemFunction.EDIT_ORGANIZATION]: Permission.EDIT,
    [SystemFunction.DELETE_ORGANIZATION]: Permission.EDIT,
    [SystemFunction.MANAGE_BILLING]: Permission.EDIT,
    [SystemFunction.VIEW_PROJECTS]: Permission.EDIT,
    [SystemFunction.CREATE_PROJECTS]: Permission.EDIT,
    [SystemFunction.EDIT_PROJECTS]: Permission.EDIT,
    [SystemFunction.DELETE_PROJECTS]: Permission.EDIT,
    [SystemFunction.VIEW_INTEGRATIONS]: Permission.EDIT,
    [SystemFunction.MANAGE_INTEGRATIONS]: Permission.EDIT,
    [SystemFunction.VIEW_KNOWLEDGE_BASE]: Permission.EDIT,
    [SystemFunction.MANAGE_KNOWLEDGE_BASE]: Permission.EDIT,
    [SystemFunction.VIEW_USERS]: Permission.EDIT,
    [SystemFunction.INVITE_USERS]: Permission.EDIT,
    [SystemFunction.MANAGE_USERS]: Permission.EDIT,
    [SystemFunction.VIEW_ANALYTICS]: Permission.READ,
    [SystemFunction.VIEW_AGENT_CONFIG]: Permission.EDIT,
    [SystemFunction.EDIT_AGENT_CONFIG]: Permission.EDIT,
  },

  [UserRole.DEVELOPER]: {
    [SystemFunction.VIEW_ORGANIZATION]: Permission.READ,
    [SystemFunction.EDIT_ORGANIZATION]: Permission.NO_ACCESS,
    [SystemFunction.DELETE_ORGANIZATION]: Permission.NO_ACCESS,
    [SystemFunction.MANAGE_BILLING]: Permission.NO_ACCESS,
    [SystemFunction.VIEW_PROJECTS]: Permission.READ,
    [SystemFunction.CREATE_PROJECTS]: Permission.EDIT,
    [SystemFunction.EDIT_PROJECTS]: Permission.EDIT,
    [SystemFunction.DELETE_PROJECTS]: Permission.EDIT,
    [SystemFunction.VIEW_INTEGRATIONS]: Permission.EDIT,
    [SystemFunction.MANAGE_INTEGRATIONS]: Permission.EDIT,
    [SystemFunction.VIEW_KNOWLEDGE_BASE]: Permission.READ,
    [SystemFunction.MANAGE_KNOWLEDGE_BASE]: Permission.EDIT,
    [SystemFunction.VIEW_USERS]: Permission.READ,
    [SystemFunction.INVITE_USERS]: Permission.NO_ACCESS,
    [SystemFunction.MANAGE_USERS]: Permission.NO_ACCESS,
    [SystemFunction.VIEW_ANALYTICS]: Permission.READ,
    [SystemFunction.VIEW_AGENT_CONFIG]: Permission.EDIT,
    [SystemFunction.EDIT_AGENT_CONFIG]: Permission.EDIT,
  },

  [UserRole.USER]: {
    [SystemFunction.VIEW_ORGANIZATION]: Permission.READ,
    [SystemFunction.EDIT_ORGANIZATION]: Permission.NO_ACCESS,
    [SystemFunction.DELETE_ORGANIZATION]: Permission.NO_ACCESS,
    [SystemFunction.MANAGE_BILLING]: Permission.NO_ACCESS,
    [SystemFunction.VIEW_PROJECTS]: Permission.READ,
    [SystemFunction.CREATE_PROJECTS]: Permission.EDIT,
    [SystemFunction.EDIT_PROJECTS]: Permission.EDIT,
    [SystemFunction.DELETE_PROJECTS]: Permission.EDIT,
    [SystemFunction.VIEW_INTEGRATIONS]: Permission.READ,
    [SystemFunction.MANAGE_INTEGRATIONS]: Permission.NO_ACCESS,
    [SystemFunction.VIEW_KNOWLEDGE_BASE]: Permission.READ,
    [SystemFunction.MANAGE_KNOWLEDGE_BASE]: Permission.EDIT,
    [SystemFunction.VIEW_USERS]: Permission.READ,
    [SystemFunction.INVITE_USERS]: Permission.NO_ACCESS,
    [SystemFunction.MANAGE_USERS]: Permission.NO_ACCESS,
    [SystemFunction.VIEW_ANALYTICS]: Permission.READ,
    [SystemFunction.VIEW_AGENT_CONFIG]: Permission.READ,
    [SystemFunction.EDIT_AGENT_CONFIG]: Permission.NO_ACCESS,
  },
};

// ===== PERMISSION CHECKER =====
/**
 * Check if a user role has permission for a system function
 * @param userRole - The user's role (admin, user, etc.)
 * @param systemFunction - The function to check (e.g., EDIT_ORGANIZATION)
 * @param requiredPermission - The required permission level (READ or EDIT)
 * @returns true if the user has the required permission, false otherwise
 */
export const hasPermission = (
  userRole: UserRole,
  systemFunction: SystemFunction,
  requiredPermission: Permission = Permission.READ,
): boolean => {
  const rolePermissions = ROLE_PERMISSIONS[userRole];
  if (!rolePermissions) return false;

  const permission = rolePermissions[systemFunction];
  if (!permission || permission === Permission.NO_ACCESS) return false;

  if (requiredPermission === Permission.EDIT) {
    return permission === Permission.EDIT;
  }

  return permission === Permission.READ || permission === Permission.EDIT;
};
